// src/pages/auth/ForgotPasswordPage.jsx

import { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { forgotPassword } from "../../services/authApi";
import logger from "../../utils/logger";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError("Veuillez saisir votre adresse e-mail.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await forgotPassword({
        email: normalizedEmail,
      });

      setMessage(
        data?.message ||
          "Si le compte existe, vous allez recevoir un e-mail de réinitialisation."
      );

      setEmail("");

      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur réseau, réessayez."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <ForgotPanel>
        <PanelBadge>Compte utilisateur</PanelBadge>

        <h1>Mot de passe oublié</h1>

        <p>
          Saisis ton adresse e-mail. Si un compte existe, un lien de
          réinitialisation te sera envoyé.
        </p>

        {message && <PanelMessage>{message}</PanelMessage>}
        {error && <PanelMessage $error>{error}</PanelMessage>}

        <Form onSubmit={handleSubmit}>
          <Field>
            <label>Adresse e-mail</label>

            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              placeholder="exemple@domaine.fr"
              required
            />
          </Field>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? "Envoi en cours..." : "Envoyer le lien de réinitialisation"}
          </SubmitButton>
        </Form>
      </ForgotPanel>
    </PageWrapper>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  display: grid;
  place-items: center;
  padding: 1rem;
  box-sizing: border-box;
  background: #0b1328;
  overflow-y: auto;

  @media (max-height: 720px) {
    align-items: start;
  }
`;

const ForgotPanel = styled.section`
  width: min(100%, 520px);
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  padding: 1.6rem;
  border-radius: 24px;
  background: #1d263d;
  color: #f7f7f7;
  box-sizing: border-box;

  h1 {
    margin: 0.8rem 0 0.5rem;
    font-size: clamp(1.35rem, 4vw, 1.65rem);
    font-weight: 950;
  }

  p {
    margin: 0 0 1.2rem;
    opacity: 0.74;
    line-height: 1.45;
  }
`;

const PanelBadge = styled.div`
  display: inline-block;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
`;

const Form = styled.form`
  display: grid;
  gap: 1rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;

  label {
    font-size: 0.8rem;
    font-weight: 850;
    opacity: 0.84;
  }

  input {
    width: 100%;
    min-height: 46px;
    border: none;
    border-radius: 12px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    outline: none;
    box-sizing: border-box;
  }

  input:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
  position: sticky;
  bottom: 0;
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;